import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { lookupInvite, setInviteToken, startGoogleLogin } from "../lib/api";
import { ArrowRight, Clock, ShieldCheck } from "lucide-react";
import CredentialForm from "../components/CredentialForm";
import SwirlingMass from "../components/SwirlingMass";

/** Landing page for a sealed link. The link authorises; Google or a password identifies. */
export default function InviteLanding() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [withEmail, setWithEmail] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    lookupInvite(token)
      .then((res) => {
        setD(res);
        if (res.valid) setInviteToken(token);
      })
      .catch(() => setD({ valid: false, reason: "Could not check this link. Try again shortly." }));
  }, [token]);

  return (
    <div data-testid="invite-landing" className="grain relative h-full overflow-hidden">
      <div className="absolute inset-0 opacity-70">
        <SwirlingMass />
      </div>

      <div className="relative flex h-full items-center justify-center px-6">
        <div className="w-full max-w-md">
          {!d && <p className="label">Checking your invitation…</p>}

          {d && !d.valid && (
            <div data-testid="invite-invalid">
              <p className="label">Invitation</p>
              <h1 className="mt-4 font-display text-3xl leading-tight tracking-tight">
                This link will not open.
              </h1>
              <p className="mt-5 text-sm leading-relaxed text-zinc-400">{d.reason}</p>
              <button
                onClick={() => nav("/")}
                data-testid="invite-home-btn"
                className="label mt-8 underline decoration-[var(--line-hi)] underline-offset-4 hover:text-zinc-200"
              >
                Back to the entrance →
              </button>
            </div>
          )}

          {d?.valid && (
            <div data-testid="invite-valid">
              <p className="label flex items-center gap-2">
                <ShieldCheck size={11} strokeWidth={1.7} /> Sealed invitation
              </p>
              <h1 className="mt-5 font-display text-4xl leading-[0.98] tracking-tight">
                You have been
                <br />
                <span className="italic text-zinc-400">invited to sit.</span>
              </h1>
              {d.label && (
                <p
                  data-testid="invite-label"
                  className="mt-6 border-l border-[var(--line-hi)] pl-4 text-sm text-zinc-200"
                >
                  {d.label}
                </p>
              )}
              <p className="mt-6 text-sm leading-relaxed text-zinc-400">
                This link is what admits you — it works once, for one person. Sign in so the
                instrument knows whose map it is drawing, then read what happens to your answers
                before the first question.
              </p>

              <button
                onClick={startGoogleLogin}
                data-testid="invite-signin-btn"
                className="btn-stark mt-9 flex w-full items-center justify-center gap-3 !py-3.5"
              >
                Continue with Google <ArrowRight size={13} strokeWidth={2} />
              </button>

              {!withEmail ? (
                <button
                  onClick={() => setWithEmail(true)}
                  data-testid="invite-email-btn"
                  className="label mt-4 underline decoration-[var(--line-hi)] underline-offset-4 hover:text-zinc-200"
                >
                  Or set up an email address and password instead →
                </button>
              ) : (
                <CredentialForm onDone={() => nav("/interview", { replace: true })} />
              )}

              {d.code && (
                <p className="mt-6 font-data text-[10px] leading-relaxed text-zinc-600">
                  your invitation code, if you are ever asked for it ·{" "}
                  <span className="tracking-[0.22em] text-zinc-300">{d.code}</span>
                </p>
              )}

              <p className="mt-2 flex items-center gap-2 font-data text-[10px] text-zinc-600">
                <Clock size={10} strokeWidth={1.6} />
                expires {d.expires_at?.slice(0, 10)} · invited by {d.invited_by}
              </p>
              <button
                onClick={() => nav("/legal")}
                data-testid="invite-legal-link"
                className="label mt-5 underline decoration-[var(--line-hi)] underline-offset-4 hover:text-zinc-200"
              >
                Read the terms, the privacy policy and the waiver first →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
